import { useEffect, useRef, useState } from "react";

const ENV_WP_URL = process.env.REACT_APP_WP_URL;

// The site serves both loansalaah.com and loansalaah.in; each domain has its own
// WordPress CMS. Resolve the matching CMS from the hostname at runtime, so one
// build serves both sites. Preview/dev falls back to the env URL.
function resolveWpUrl() {
  if (typeof window !== "undefined") {
    const h = window.location.hostname;
    if (/(^|\.)loansalaah\.com$/.test(h)) return "https://cms.loansalaah.com";
    if (/(^|\.)loansalaah\.in$/.test(h)) return "https://cms.loansalaah.in";
  }
  return ENV_WP_URL;
}

const WP_URL = resolveWpUrl();

export async function wpFetch(path, { timeout = 9000 } = {}) {
  const attempt = async (base) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeout);
    try {
      const res = await fetch(`${base}/wp-json${path}`, { signal: controller.signal });
      if (!res.ok) throw new Error(`WP ${res.status}`);
      return await res.json();
    } finally {
      clearTimeout(timer);
    }
  };
  try {
    return await attempt(WP_URL);
  } catch (e) {
    // During the cms.loanmantri.com → cms.loansalaah.com migration, fall back to
    // the env-configured CMS so content keeps flowing from whichever is live.
    if (ENV_WP_URL && ENV_WP_URL !== WP_URL) return attempt(ENV_WP_URL);
    throw e;
  }
}

export function stripHtml(html) {
  const doc = new DOMParser().parseFromString(html || "", "text/html");
  return (doc.body.textContent || "").trim();
}

let categoriesPromise = null;
export function getCategories() {
  if (!categoriesPromise) {
    categoriesPromise = wpFetch(`/wp/v2/categories?per_page=100`).catch(() => []);
  }
  return categoriesPromise;
}

export function mapPost(p) {
  const media = p._embedded?.["wp:featuredmedia"]?.[0];
  const termGroups = p._embedded?.["wp:term"] || [];
  const terms = termGroups.flat ? termGroups.flat() : [];
  const author = p._embedded?.author?.[0];
  return {
    id: p.id,
    slug: p.slug,
    title: stripHtml(p.title?.rendered || ""),
    excerpt: stripHtml(p.excerpt?.rendered || ""),
    content: p.content?.rendered || "",
    image: media?.source_url || "",
    date: p.date,
    categories: terms.filter((t) => t.taxonomy === "category").map((t) => t.name),
    tags: terms.filter((t) => t.taxonomy === "post_tag").map((t) => ({ name: t.name, slug: t.slug })),
    authorName: author?.name || "",
    authorAvatar: author?.avatar_urls?.["96"] || author?.avatar_urls?.["48"] || "",
    authorBio: author?.description || "",
  };
}

export async function fetchSiteIdentity() {
  const d = await wpFetch("");
  // A fresh WP install's default title is the bare host (e.g. "cms.loansalaah.com") —
  // that's not a brand name, so keep the built-in identity until the owner sets one.
  const host = new URL(WP_URL).host;
  const name = d.name && d.name !== host ? d.name : "Loan Salaah";
  return {
    name,
    tagline: d.description || "",
    icon: d.site_icon_url || "",
    url: d.url || "",
  };
}

export async function fetchPostsByCategorySlug(slug, perPage = 20) {
  const cats = await getCategories();
  const cat = cats.find((c) => c.slug === slug);
  if (!cat) return [];
  const posts = await wpFetch(`/wp/v2/posts?categories=${cat.id}&per_page=${perPage}&_embed=1&orderby=date&order=asc`);
  return posts.map(mapPost);
}

export async function fetchHeroSlides() {
  const posts = await fetchPostsByCategorySlug("hero-slides", 10);
  return posts.map((p) => ({
    id: p.id,
    eyebrow: "Loan Salaah · DSA & Card Marketplace",
    title: p.title,
    subtitle: p.excerpt,
    image_url: p.image,
    cta_label: "Apply Now",
    cta_href: "/apply",
  }));
}

export async function fetchPartners() {
  const posts = await fetchPostsByCategorySlug("partners", 50);
  return posts.map((p) => ({
    id: p.id,
    name: p.title,
    initials: p.title.split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase(),
    image: p.image,
  }));
}

export async function fetchBlogPosts(perPage = 24) {
  const cats = await getCategories();
  const excluded = cats.filter((c) => ["hero-slides", "partners"].includes(c.slug)).map((c) => c.id);
  const q = excluded.length ? `&categories_exclude=${excluded.join(",")}` : "";
  const posts = await wpFetch(`/wp/v2/posts?per_page=${perPage}&_embed=1${q}`);
  // Never surface WordPress's default sample post — the fallback library stays
  // until the owner publishes real content.
  return posts.filter((p) => p.slug !== "hello-world").map(mapPost);
}

export async function fetchPostBySlug(slug) {
  const posts = await wpFetch(`/wp/v2/posts?slug=${encodeURIComponent(slug)}&_embed=1`);
  return posts.length ? mapPost(posts[0]) : null;
}

export async function fetchPages() {
  const pages = await wpFetch(`/wp/v2/pages?per_page=100&_embed=1&orderby=menu_order&order=asc`);
  return pages.map(mapPost);
}

export async function fetchPageBySlug(slug) {
  const pages = await wpFetch(`/wp/v2/pages?slug=${encodeURIComponent(slug)}&_embed=1`);
  return pages.length ? mapPost(pages[0]) : null;
}

export async function submitEnquiryCF7(fields) {
  const forms = await wpFetch(`/contact-form-7/v1/contact-forms`);
  if (!Array.isArray(forms) || forms.length === 0) throw new Error("cf7-missing");
  const form = forms.find((f) => /enquiry|apply|loan|card/i.test(f.title || f.slug || "")) || forms[0];
  const fd = new FormData();
  Object.entries(fields).forEach(([k, v]) => fd.append(k, String(v ?? "")));
  const res = await fetch(`${WP_URL}/wp-json/contact-form-7/v1/contact-forms/${form.id}/feedback`, {
    method: "POST",
    body: fd,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || (data.status && data.status !== "mail_sent")) {
    throw new Error(data.message || "cf7-failed");
  }
  return data;
}

export function useWP(fetcher, fallback, deps = []) {
  const [data, setData] = useState(fallback);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  useEffect(() => {
    let live = true;
    fetcherRef
      .current()
      .then((d) => {
        if (live && d && (!Array.isArray(d) || d.length > 0)) setData(d);
      })
      .catch(() => {});
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return data;
}
